import { useCallback, useState } from "react";
import { useRecorder } from "../hooks/useRecorder";
import { WaveformTrimmer } from "./WaveformTrimmer";
import { trimAudioBlob } from "../lib/audioUtils";
import { Icon } from "./icons";

interface AudioRecorderProps {
  onRecorded: (blob: Blob, duration: number) => void;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function AudioRecorder({ onRecorded }: AudioRecorderProps) {
  const { isRecording, duration, audioBlob, audioUrl, start, stop, reset } = useRecorder();
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [trimming, setTrimming] = useState(false);

  const handleTrim = useCallback((startTime: number, endTime: number) => {
    setTrimStart(startTime);
    setTrimEnd(endTime);
  }, []);

  const handleAccept = async () => {
    if (!audioBlob) return;
    const trimmedDuration = trimEnd - trimStart;
    const isFullSelection = trimStart < 0.05 && Math.abs(trimEnd - duration) < 0.05;
    if (isFullSelection || trimmedDuration <= 0) {
      onRecorded(audioBlob, duration);
      return;
    }
    setTrimming(true);
    try {
      const trimmedBlob = await trimAudioBlob(audioBlob, trimStart, trimEnd);
      onRecorded(trimmedBlob, trimmedDuration);
    } catch (err) {
      console.error("Trim failed, using original:", err);
      onRecorded(audioBlob, duration);
    } finally {
      setTrimming(false);
    }
  };

  const handleReset = () => {
    setTrimStart(0);
    setTrimEnd(0);
    reset();
  };

  const trimmedDuration = trimEnd - trimStart;

  return (
    <div className="recorder">
      {isRecording && (
        <div className="recorder-stage recording">
          <div className="recorder-pulse">
            <div className="recorder-mic">
              <Icon.Mic />
            </div>
          </div>
          <p className="recorder-time">{formatTime(duration)}</p>
          <button type="button" className="btn btn-danger" onClick={stop}>
            <span style={{ width: 8, height: 8, background: "currentColor", borderRadius: 2 }} />
            Stop recording
          </button>
        </div>
      )}

      {!isRecording && audioUrl && (
        <div className="stack-v">
          <WaveformTrimmer audioUrl={audioUrl} onTrim={handleTrim} />
          <p
            style={{
              fontSize: 12,
              color: "var(--ink-3)",
              textAlign: "center",
              fontFamily: "JetBrains Mono, monospace",
              margin: 0,
            }}
          >
            {trimmedDuration > 0 && trimmedDuration < duration - 0.1
              ? `Trimmed: ${formatTime(trimmedDuration)} (original: ${formatTime(duration)})`
              : `Duration: ${formatTime(duration)}`}
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className="btn btn-ghost" style={{ flex: 1, justifyContent: "center" }} onClick={handleReset}>
              <Icon.Refresh /> Re-record
            </button>
            <button
              type="button"
              className={`btn btn-primary${trimming ? " disabled" : ""}`}
              style={{ flex: 1, justifyContent: "center" }}
              onClick={handleAccept}
            >
              {trimming ? (
                <>
                  <span className="spin" /> Trimming…
                </>
              ) : (
                <>
                  <Icon.Check /> Use recording
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {!isRecording && !audioUrl && (
        <div className="recorder-stage idle">
          <button
            type="button"
            className="recorder-start"
            onClick={start}
            aria-label="Start recording"
          >
            <Icon.Mic />
          </button>
          <p style={{ fontSize: 13, color: "var(--ink-3)", margin: 0 }}>Tap to start recording</p>
        </div>
      )}
    </div>
  );
}
